import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { LoginService } from 'src/app/auth/login.service';
import { HeaderService } from 'src/app/core/services/header.service';
import { StockService } from 'src/app/core/services/stock.service';
import { AuthDialogComponent } from 'src/app/shared/components/auth-dialog/auth-dialog.component';
import { environment } from 'src/environments/environment';

interface Buyer {
  user_id: number;
  user_code: string;
}

interface StockDetailItem {
  id?: number | null;
  name: string;
  type?: string;
  unit?: string;
  quantity?: number | null;
  soldQty?: number | null;
  retailPrice?: number | null;
  wholesaleTotal?: number | null;
  totalRevenue?: number | null;
  totalProfit?: number | null;
  note?: string;
  existingImages?: string[];
  isActive?: boolean;
  isChecked?: boolean;
  buyers?: Buyer[];
  revenue?: number | null;
  profit?: number | null;

}

interface PreviewItem {
  preview: string;
  name: string;
  type: string;
}

@Component({
  selector: 'app-stock-detail-modal',
  templateUrl: './stock-detail-modal.component.html',
  styleUrls: ['./stock-detail-modal.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class StockDetailModalComponent implements OnDestroy, OnInit {
  @Input() isAdmin = false;

  @Input() openFrom: 'userstock' | 'medialist' = 'medialist';

  @Output() contractSigned = new EventEmitter<any>();

  @ViewChild(AuthDialogComponent) authDialog?: AuthDialogComponent;

  isVisible = false;
  isContractModalVisible = false;
  loadingDetail = false;
  signing = false;
  errorMessage = '';

  detailData: {
    id: number | null;
    title: string;
    warehouseName: string;
    description: string;
    totalRevenue?: number | null;
    totalWholesale?: number | null;
    totalProfit?: number | null;
    items: StockDetailItem[];
    stockOwnerId?: number | null;
  } = {
      id: null,
      title: 'KẾ HOẠCH KINH DOANH',
      warehouseName: 'TỔNG KHO MỸ PHẨM NỘI ĐỊA',
      description: '',
      items: [],
      stockOwnerId: null,
    };

  existingMediaByItem: string[][] = [];
  activePreviewIndexByItem: number[] = [];
  selectedItemIndexes: boolean[] = [];
  userCode = '';
  userId = null;
  private pendingContractOpen = false;

  contractContent = {
    title: 'HỢP ĐỒNG ĐIỆN TỬ XÁC NHẬN THAM GIA',
    content: [
      'Tên tài khoản: ………………………………………',
      'Người tham gia đồng ý sử dụng tài khoản trong hệ thống để tham gia các lô hàng kinh doanh online.',
      '',
      '📌 CAM KẾT HỆ THỐNG',
      'Minh bạch toàn bộ đơn hàng và doanh thu',
      'Ghi nhận doanh thu khi đơn hàng hoàn tất',
      'Hỗ trợ vận hành và xử lý đơn theo quy trình hệ thống',
      '',
      '📌 PHẠM VI LÔ HÀNG',
      '500.000 – 3.000.000 VNĐ: ưu tiên xử lý trong ngày',
      'Từ 5.000.000 VNĐ trở lên: phân phối đa kênh, xử lý dự kiến 2–7 ngày',
      '',
      '📌 QUY ĐỊNH NGƯỜI THAM GIA',
      'Không gian lận, không tạo đơn ảo',
      'Không giao dịch ngoài hệ thống',
      'Tuân thủ quy trình vận hành',
      '',
      '📌 HIỆU LỰC',
      'Có hiệu lực ngay khi bắt đầu sử dụng tài khoản',
      'Tiếp tục tham gia = đồng ý toàn bộ điều khoản',
      '',
      '✅ XÁC NHẬN',
      'Người tham gia xác nhận đã đọc và đồng ý với nội dung hợp đồng điện tử này.'
    ]
  };

  constructor(
    private cdr: ChangeDetectorRef,
    private stockService: StockService,
    private loginService: LoginService,
    private headerService: HeaderService,
  ) { }

  ngOnInit(): void {
    this.userCode = (this.loginService.getUserCode() || '').toLowerCase();
    console.log('tag', JSON.stringify(this.loginService.getUserDetail()))
    this.userId = this.loginService.getUserDetail()?.id || null;
  }

  ngOnDestroy(): void {
    document.body.classList.remove('overflow-hidden');
  }

  get isAdminUser(): boolean {
    return this.userCode === 'admin';
  }

  get isLoggedIn(): boolean {
    return !!this.userCode;
  }

  isCurrentUserOwner(item: StockDetailItem): boolean {
    if (!this.userCode || !Array.isArray(item?.buyers) || item.buyers.length === 0) {
      return false;
    }
    return item.buyers.some(buyer => buyer.user_code?.toLowerCase() === this.userCode);
  }

  get canShowContractButton(): boolean {
    return !this.isAdminUser;
  }

  get contractButtonLabel(): string {
    return this.isLoggedIn ? 'Ký hợp đồng' : 'Đăng nhập để ký';
  }

  get isContractDisabled(): boolean {
    const userCode = this.loginService.getUserCode();
    if (!userCode) return true;
    if (userCode === 'admin') return true;

    const hasSelected = (this.detailData.items || []).some(i => i.isChecked);
    const hasValidQty = (this.detailData.items || [])
      .filter(i => i.isChecked)
      .every(i => i.quantity && i.quantity > 0);

    return !hasSelected || !hasValidQty;
  }

  open(stockId?: number, source: 'userstock' | 'medialist' = 'medialist'): void {
    this.openFrom = source;

    this.isVisible = true;
    this.errorMessage = '';
    this.loadingDetail = false;
    this.signing = false;
    this.isContractModalVisible = false;

    document.body.classList.add('overflow-hidden');

    if (stockId) {
      this.loadData(stockId);
    } else {
      this.resetDetailData();
      this.cdr.markForCheck();
    }
  }

  private loadData(stockId: number): void {
    this.loadingDetail = true;
    this.errorMessage = '';
    this.cdr.markForCheck();
    const isFromUserStock = this.openFrom === 'userstock';
    this.stockService.getStock({ id: stockId, userId: this.userId }).subscribe({
      next: (res: any) => {
        const data = res?.data || {};
        const productItems: StockDetailItem[] = Array.isArray(data?.items) ? data.items : [];

        this.detailData = {
          id: data?.id ?? stockId,
          title: data?.title ?? 'KẾ HOẠCH KINH DOANH',  
          warehouseName: data?.warehouseName ?? 'TỔNG KHO MỸ PHẨM NỘI ĐỊA',
          description: data?.description ?? '',
          totalRevenue: data?.totalRevenue ?? null,
          totalWholesale: data?.totalWholesale ?? null,
          totalProfit: data?.totalProfit ?? null,
          stockOwnerId: data?.userId ?? null,
          items: productItems.map((item: any) => {
            let buyers: Buyer[] = [];
            if (item?.buyers) {
              try {
                buyers = typeof item.buyers === 'string' ? JSON.parse(item.buyers) : item.buyers;
              } catch (e) {
                console.warn('Failed to parse buyers:', item.buyers);
                buyers = [];
              }
            }
            return {
              id: item?.id ?? null,
              name: item?.name ?? '',
              type: item?.type ?? '',
              unit: item?.unit ?? 'sản phẩm',
              isActive: !!item?.isActive,
              isChecked: false,
              quantity: item?.quantity ,
              soldQty: item?.soldQty ?? null,
              retailPrice: item?.retailPrice ?? null,
              wholesaleTotal: item?.wholesaleTotal ?? null,
              totalRevenue: item?.totalRevenue ?? null,
              totalProfit: item?.totalProfit ?? null,
              note: item?.note ?? '',
              existingImages: Array.isArray(item?.existingImages) ? item.existingImages : [],
              buyers: buyers,
              revenue: item?.revenue ?? null,
              profit: item?.profit ?? null,
            };
          }),
        };

        this.existingMediaByItem = (this.detailData.items || []).map((item) =>
          (Array.isArray(item?.existingImages) ? item.existingImages : [])
            .filter((path: string) => !!path)
            .map((path: string) => this.buildImageUrl(path))
        );

        this.activePreviewIndexByItem = this.detailData.items.map(() => 0);
        this.selectedItemIndexes = this.detailData.items.map(() => false);

        this.loadingDetail = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Không thể tải dữ liệu chi tiết kho:', err);
        this.errorMessage = 'Không thể tải dữ liệu. Vui lòng thử lại.';
        this.loadingDetail = false;
        this.resetDetailData();
        this.cdr.markForCheck();
      },
    });
  }

  private resetDetailData(): void {
    this.detailData = {
      id: null,
      title: 'KẾ HOẠCH KINH DOANH',
      warehouseName: 'TỔNG KHO MỸ PHẨM NỘI ĐỊA',
      description: '',
      items: [],
    };
    this.existingMediaByItem = [];
    this.activePreviewIndexByItem = [];
    this.selectedItemIndexes = [];
  }

  isItemSelected(itemIndex: number): boolean {
    return !!this.selectedItemIndexes[itemIndex];
  }

  toggleItemSelection(itemIndex: number): void {
    this.selectedItemIndexes[itemIndex] = !this.selectedItemIndexes[itemIndex];
    this.cdr.markForCheck();
  }

  close(): void {
    this.isVisible = false;
    this.isContractModalVisible = false;
    this.errorMessage = '';
    this.loadingDetail = false;
    this.signing = false;
    document.body.classList.remove('overflow-hidden');
    this.cdr.markForCheck();
  }

  openContractModal(): void {
    if (this.isAdminUser) {
      return;
    }

    if (!this.isLoggedIn) {
      this.pendingContractOpen = true;
      this.authDialog?.open('login');
      return;
    }

    this.isContractModalVisible = true;
    document.body.classList.add('overflow-hidden');
    this.cdr.markForCheck();
  }

  handleContractClick(): void {
    if (this.isContractDisabled) return;

    const filteredItems = (this.detailData.items || []).filter(item => item.isChecked);

    if (filteredItems.length < 1) {
      alert('Vui lòng chọn ít nhất một kho hàng để ký hợp đồng.');
      return;
    }

    const invalidItems = filteredItems.filter(item =>
      !item.quantity || item.quantity <= 0
    );

    if (invalidItems.length > 0) {
      alert('Vui lòng nhập số lượng cho tất cả kho hàng đã chọn.');
      return;
    }

    this.openContractModal();
  }

  handleAuthLoggedIn(): void {
    this.userCode = (this.loginService.getUserCode() || '').toLowerCase();
    this.headerService.requestRefresh();
    if (this.pendingContractOpen) {
      this.pendingContractOpen = false;
      this.openContractModal();
    }
  }

  closeContractModal(): void {
    this.isContractModalVisible = false;
    if (this.isVisible) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
    this.cdr.markForCheck();
  }

  signContract(): void {
    if (this.signing || this.isAdminUser || !this.isLoggedIn) {
      return;
    }
    this.signing = true;
    this.errorMessage = '';
    this.cdr.markForCheck();
    const filteredItems = (this.detailData.items || [])
      .filter(item => item.isChecked && Number(item.quantity ) > 0);

    if (filteredItems.length < 1) {
      alert('Vui lòng chọn ít nhất một kho hàng để ký hợp đồng và số lượng phải lớn hơn 0.');
      this.signing = false;
      this.cdr.markForCheck();
      return;
    }

    const payload = {
      id: this.detailData.id,
      title: this.detailData.title,
      warehouseName: this.detailData.warehouseName,
      items: filteredItems,
      // totalRevenue: this.totalRevenue,
      // totalWholesale: this.totalWholesale,
      // totalProfit: this.totalProfit
    };
    this.stockService.contract(payload).subscribe({
      next: (res: any) => {
        if (res?.message === 'MONEY_NOT_ENOUGH') {
          this.signing = false;
          this.cdr.markForCheck();
          alert('Số dư không đủ để ký hợp đồng. Vui lòng nạp thêm tiền vào tài khoản.');
          return;
        }
        this.headerService.requestRefresh();
        this.contractSigned.emit(payload);
        this.closeContractModal();
        this.signing = false;
        this.cdr.markForCheck();
        if (this.detailData.id) {
          this.loadData(this.detailData.id);
        }
        alert('Đăng ký hợp đồng thành công.');
      },
      error: (err) => {
        this.signing = false;
        console.error('Lỗi khi ký hợp đồng:', err);
        const errorMessage = err?.error?.message || err?.message || 'Không thể ký hợp đồng. Vui lòng thử lại.';
        alert(errorMessage);
        this.cdr.markForCheck();
      },
    });
  }

  getPreviewItems(itemIndex: number): PreviewItem[] {
    return (this.existingMediaByItem[itemIndex] || []).map((url, index) => ({
      preview: url,
      name: `Tệp ${index + 1}`,
      type: this.detectMediaType(url),
    }));
  }

  getActivePreview(itemIndex: number): PreviewItem | null {
    const previewItems = this.getPreviewItems(itemIndex);
    if (!previewItems.length) return null;
    const activeIndex = this.activePreviewIndexByItem[itemIndex] || 0;
    if (activeIndex < 0 || activeIndex >= previewItems.length) {
      return previewItems[0];
    }
    return previewItems[activeIndex];
  }

  setActivePreview(itemIndex: number, previewIndex: number): void {
    const previewItems = this.getPreviewItems(itemIndex);
    if (previewIndex < 0 || previewIndex >= previewItems.length) return;

    this.activePreviewIndexByItem[itemIndex] = previewIndex;
    this.cdr.markForCheck();
  }

  onSwipeLeft(itemIndex: number): void {
    const currentIndex = this.activePreviewIndexByItem[itemIndex] || 0;
    const previewItems = this.getPreviewItems(itemIndex);
    const nextIndex = (currentIndex + 1) % previewItems.length;
    this.setActivePreview(itemIndex, nextIndex);
  }

  onSwipeRight(itemIndex: number): void {
    const currentIndex = this.activePreviewIndexByItem[itemIndex] || 0;
    const previewItems = this.getPreviewItems(itemIndex);
    const prevIndex = currentIndex === 0 ? previewItems.length - 1 : currentIndex - 1;
    this.setActivePreview(itemIndex, prevIndex);
  }

  formatCurrency(value: any): string {
    if (value === null || value === undefined) return '';
    return new Intl.NumberFormat('vi-VN').format(value || 0);
  }

  trackByIndex(index: number): number {
    return index;
  }

  private detectMediaType(url: string): string {
    const lower = (url || '').toLowerCase();
    if (/\.(mp4|webm|ogg|mov|m4v)(\?|$)/.test(lower)) {
      return 'video/mp4';
    }
    return 'image/jpeg';
  }

  private safeJoinUrl(base: string, path: string): string {
    if (!base) return path || '';
    if (!path) return base;
    if (/^https?:\/\//i.test(path)) return path;
    return `${base.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
  }

  private normalizeUploadPath(rawPath: string): string {
    if (!rawPath) return '';
    let normalized = rawPath.replace(/\\/g, '/');
    const marker = '/uploads/';
    const markerIndex = normalized.lastIndexOf(marker);
    if (markerIndex !== -1) {
      normalized = normalized.substring(markerIndex + 1);
    }
    return normalized.replace(/^\/+/, '');
  }

  private buildImageUrl(path: string): string {
    if (!path) return '';
    if (/^https?:\/\//i.test(path)) return path;
    return this.safeJoinUrl(environment.apiUrl, this.normalizeUploadPath(path));
  }

  onQuantityChange(event: any, index: number): void {
    const numericValue = event.target.value.replace(/[^0-9]/g, '');
    const qty = numericValue ? Number(numericValue) : 0;
    const items = [...this.detailData.items];
    items[index] = {
      ...items[index],
      quantity: qty
    };
    this.detailData.items = items;
    this.cdr.markForCheck();
  }

  onFormattedNumberInput(event: any, index: number, field: string): void {
    const value = event.target.value.replace(/[^0-9]/g, '');
    const num = value ? Number(value) : null;
    this.detailData.items[index] = {
      ...this.detailData.items[index],
      [field]: num
    };
    this.cdr.markForCheck();
  }
  getItemWholesale(item: StockDetailItem): number {
      const quantity = Number(item.quantity || 0);
    const retailPrice = Number(item.wholesaleTotal || 1);
    return quantity * retailPrice;

  }
  getItemProfit(item: StockDetailItem): number {
    return this.getItemRevenue(item) - this.getItemWholesale(item);
  }
  getItemRevenue(item: StockDetailItem): number {
    const quantity = Number(item.quantity || 0);
    const retailPrice = Number(item.retailPrice || 1);
    return quantity * retailPrice;
  }
  getTotalRevenue(): number {
  return (this.detailData?.items || []).reduce((sum, item) => {
    return sum + this.getItemRevenue(item);
  }, 0);
}

getTotalWholesale(): number {
  return (this.detailData?.items || []).reduce((sum, item) => {
    return sum + this.getItemWholesale(item);
  }, 0);
}

getTotalProfit(): number {
  return this.getTotalRevenue() - this.getTotalWholesale();
}

getTotalSoldQty(): number {
  return (this.detailData?.items || []).reduce((sum, item) => {
    return sum + Number(item.soldQty || 0);
  }, 0);
}
}
